import { useState } from "react";
import { useParams } from "react-router-dom";
import { useFetch } from "../lib/useFetch";
import api from "../lib/api";
import { PageHeader, Panel, Stat, Loading, Chip } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { Sparkles, FileText, UploadCloud, CheckCircle2, GitBranch, AlertTriangle, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const TABS = [
  { key: "explore", label: "Explore" }, { key: "search", label: "Search" },
  { key: "documents", label: "Documents" }, { key: "upload", label: "Upload" },
  { key: "approvals", label: "Approvals" }, { key: "health", label: "Health" },
];

const statusChip = (s) => ({ active: "bg-emerald-100 text-emerald-700", expiring: "bg-amber-100 text-amber-700", expired: "bg-red-100 text-red-700" }[s] || "bg-slate-100 text-slate-600");
const apprChip = (s) => ({ approved: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", archived: "bg-slate-100 text-slate-500" }[s] || "");

export default function KnowledgeBase() {
  const { tab = "explore" } = useParams();
  return (
    <div data-testid="kb-page">
      <PageHeader title="Knowledge Base" subtitle="Enterprise SOPs, policies and manuals with versioning, approvals and AI search." />
      <SubTabs base="/kb" tab={tab} items={TABS} />
      {tab === "explore" && <Explore />}
      {tab === "search" && <KBSearch />}
      {tab === "documents" && <Documents />}
      {tab === "upload" && <Upload />}
      {tab === "approvals" && <Approvals />}
      {tab === "health" && <Health />}
    </div>
  );
}

function Explore() {
  const { data, loading } = useFetch("/kb/explore", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total Documents" value={data.stats.total} />
        <Stat label="Active" value={data.stats.active} tone="text-emerald-600" />
        <Stat label="Expiring Soon" value={data.stats.expiring} tone="text-amber-600" />
        <Stat label="Expired" value={data.stats.expired} tone="text-red-600" />
        <Stat label="Pending Approval" value={data.stats.pending} tone="text-primary" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.categories.filter((c) => c.count > 0).map((c) => (
          <div key={c.name} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /><span className="text-sm font-semibold text-slate-800">{c.name}</span></div>
            <div className="kpi-num text-2xl font-extrabold text-slate-900 mt-2">{c.count}</div>
            <div className="text-xs text-slate-400 mt-1">{c.active} active · {c.expiring} expiring · {c.pending} pending</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KBSearch() {
  const [q, setQ] = useState("What is the chemical dosage for a 50 KG cotton load?");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setRes(null);
    try { const { data } = await api.post("/kb/search", { query: q }); setRes(data); }
    catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <form onSubmit={run} className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the knowledge base…"
          className="w-full h-12 pl-10 pr-28 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="kb-search-input" />
        <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary text-white text-sm px-4 py-1.5 rounded-md" data-testid="kb-search-btn">Ask KB</button>
      </form>
      {loading && <div className="text-sm text-slate-400 animate-pulse">Retrieving from knowledge base…</div>}
      {res && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="kb-search-result">
          <div className="border-l-2 border-secondary bg-secondary/5 p-5">
            <div className="flex items-center gap-2 text-xs font-semibold text-primary mb-2"><Sparkles className="w-4 h-4" /> Answer</div>
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{res.answer}</p>
          </div>
          {res.source_doc && (
            <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
              <span><b className="text-slate-700">Source:</b> {res.source_doc} {res.version}</span>
              <span><b className="text-slate-700">Effective:</b> {res.effective_date}</span>
              <span><b className="text-slate-700">Category:</b> {res.category}</span>
              <span className="ml-auto font-semibold text-emerald-600">Confidence {res.confidence}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Documents() {
  const { data, loading } = useFetch("/kb/documents", []);
  const [active, setActive] = useState(null);
  if (loading || !data) return <Loading rows={5} />;
  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.map((d) => (
          <div key={d.id} onClick={() => setActive(d)} className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:shadow-sm transition-[box-shadow]" data-testid={`kb-doc-${d.id}`}>
            <div className="flex items-start justify-between">
              <FileText className="w-5 h-5 text-primary" />
              <div className="flex gap-1"><Chip className={statusChip(d.status)}>{d.status}</Chip></div>
            </div>
            <div className="text-sm font-semibold text-slate-800 mt-2">{d.name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{d.category} · {d.current_version}</div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <Chip className={apprChip(d.approval_status)}>{d.approval_status}</Chip>
              <span>Effective {d.effective_date}</span>
            </div>
          </div>
        ))}
      </div>
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin" data-testid="kb-doc-drawer">
          {active && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{active.name}</SheetTitle></SheetHeader>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Chip className={statusChip(active.status)}>{active.status}</Chip>
                <Chip className={apprChip(active.approval_status)}>{active.approval_status}</Chip>
                <Chip className="bg-slate-100 text-slate-600">{active.category}</Chip>
              </div>
              <p className="text-sm text-slate-600 mt-3">{active.description}</p>
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <Meta k="Location" v={active.location} /><Meta k="Department" v={active.department} />
                <Meta k="Machine" v={active.machine || "—"} /><Meta k="Uploaded by" v={active.uploaded_by} />
                <Meta k="Effective" v={active.effective_date} /><Meta k="Expiry" v={active.expiry_date} />
                <Meta k="Usage count" v={active.usage_count} /><Meta k="Current version" v={active.current_version} />
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase mb-2"><GitBranch className="w-3.5 h-3.5" /> Version History</div>
                <div className="space-y-2">
                  {active.versions.map((v, i) => (
                    <div key={v.version} className={cn("border rounded-md p-3", i === 0 ? "border-primary/30 bg-accent" : "border-slate-100")}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-800">{v.version} {i === 0 && <Chip className="bg-primary text-white ml-1">CURRENT</Chip>}</span>
                        <span className="text-xs text-slate-400">{v.effective_date}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{v.change_summary}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function Meta({ k, v }) {
  return <div className="bg-slate-50 rounded-md px-3 py-2"><div className="text-slate-400">{k}</div><div className="font-medium text-slate-700">{v}</div></div>;
}

const PIPELINE = ["Upload", "Validate", "Extract Text", "Classify", "Extract Metadata", "Chunk", "Embed", "Index", "Approval"];

function Upload() {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Customer Service", description: "", version: "v1.0", location: "All Hubs", department: "Operations" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const cats = ["Customer Service", "Pricing", "Retail SOP", "Factory SOP", "Laundry Processing", "Chemical Instructions", "Machine Manuals", "Quality Control", "Escalation", "HR", "Training", "Compensation", "Complaint Handling"];

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !form.name) { toast.error("Add a file and document name"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    try {
      const { data } = await api.post("/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); toast.success("Document processed & indexed");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
      <Panel title="Upload Document">
        <form onSubmit={submit} className="space-y-3" data-testid="kb-upload-form">
          <label className="block border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-primary/40">
            <UploadCloud className="w-8 h-8 text-slate-300 mx-auto" />
            <div className="text-sm text-slate-500 mt-2">{file ? file.name : "Click to select a PDF or text file"}</div>
            <input type="file" accept=".pdf,.txt,.md" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="kb-file-input" />
          </label>
          <Input placeholder="Document name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kb-name" />
          <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kb-category">
            {cats.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button disabled={busy} className="w-full h-10 bg-primary text-white rounded-md text-sm font-medium disabled:opacity-60" data-testid="kb-upload-submit">
            {busy ? "Processing…" : "Upload & Index"}
          </button>
        </form>
      </Panel>
      <Panel title="Processing Pipeline">
        <div className="space-y-2">
          {PIPELINE.map((step, i) => {
            const done = result || busy;
            return (
              <div key={step} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className={cn("w-4 h-4", result ? "text-emerald-500" : "text-slate-300")} />
                <span className={result ? "text-slate-700" : "text-slate-400"}>{step}</span>
              </div>
            );
          })}
        </div>
        {result && (
          <div className="mt-4 bg-secondary/5 border-l-2 border-secondary rounded p-3 text-sm">
            <div className="font-semibold text-slate-800">{result.document.name}</div>
            <div className="text-xs text-slate-500 mt-1">{result.chunks_created} chunks indexed · status {result.document.approval_status}</div>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Approvals() {
  const [key, setKey] = useState(0);
  const { data, loading } = useFetch("/kb/approvals", [key]);
  const approve = async (id) => { await api.post(`/kb/documents/${id}/approve`); toast.success("Approved & published"); setKey((k) => k + 1); };
  if (loading || !data) return <Loading rows={3} />;
  if (!data.length) return <Panel><div className="text-sm text-slate-400 py-6 text-center">No documents pending approval.</div></Panel>;
  return (
    <div className="space-y-3 max-w-3xl">
      {data.map((d) => (
        <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-testid={`kb-approval-${d.id}`}>
          <div>
            <div className="text-sm font-semibold text-slate-800">{d.name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{d.category} · {d.current_version} · by {d.uploaded_by}</div>
          </div>
          <button onClick={() => approve(d.id)} className="bg-secondary text-slate-900 text-sm font-medium px-4 py-2 rounded-md hover:bg-secondary/90" data-testid={`kb-approve-${d.id}`}>Approve & Publish</button>
        </div>
      ))}
    </div>
  );
}

function Health() {
  const { data, loading } = useFetch("/kb/health", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat label="Total" value={data.total} />
        <Stat label="Active" value={data.active} tone="text-emerald-600" />
        <Stat label="Expiring" value={data.expiring} tone="text-amber-600" />
        <Stat label="Expired" value={data.expired} tone="text-red-600" />
        <Stat label="Pending" value={data.pending} tone="text-primary" />
        <Stat label="Duplicates" value={data.duplicates} />
        <Stat label="Conflicts" value={data.conflicts} tone="text-red-600" />
      </div>
      <Panel title="AI Findings">
        <div className="space-y-2">
          {data.ai_findings.map((f, i) => (
            <div key={i} className={cn("flex gap-3 rounded-md p-3 border", f.severity === "warning" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200")}>
              <AlertTriangle className={cn("w-4 h-4 shrink-0 mt-0.5", f.severity === "warning" ? "text-amber-600" : "text-blue-600")} />
              <p className="text-sm text-slate-700">{f.text}</p>
            </div>
          ))}
        </div>
      </Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Most Used Documents">
          <div className="space-y-2">{data.most_used.map((d) => (
            <div key={d.id} className="flex justify-between text-sm border border-slate-100 rounded-md px-3 py-2"><span className="text-slate-700">{d.name}</span><span className="font-semibold text-primary">{d.usage_count}</span></div>
          ))}</div>
        </Panel>
        <Panel title="Rarely Used Documents">
          <div className="space-y-2">{data.never_used.map((d) => (
            <div key={d.id} className="flex justify-between text-sm border border-slate-100 rounded-md px-3 py-2"><span className="text-slate-700">{d.name}</span><span className="text-slate-400">{d.usage_count}</span></div>
          ))}</div>
        </Panel>
      </div>
    </div>
  );
}
